/**
 * Small pieces shared by the chart settings tabs (screenshots/76–79): a labelled row, a section, a text field that
 * commits on ↵ / blur, and a template field whose {tokens} show as blue chips (77 Subtitle, Tooltip).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

export const Section = ({ title, children, id }: { title: ReactNode; children: ReactNode; id?: string }) => (
  <section className="cs-sec" aria-labelledby={id}><h3 className="eyebrow" id={id}>{title}</h3>{children}</section>
);

export const Row = ({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) => (
  <div className={`cs-row${className ? ` ${className}` : ""}`}><span className="cs-row__k">{label}</span>{children}</div>
);

/** Segmented control (the design's View Switch) for 2–4 options. */
export function Seg<T extends string>({ options, value, onChange, label, wide }: {
  options: readonly { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void; label: string; wide?: boolean;
}) {
  return (
    <div className={`cs-seg${wide ? " cs-seg--wide" : ""}`} role="radiogroup" aria-label={label}
      onKeyDown={(e) => {
        const d = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const i = options.findIndex((o) => o.id === value);
        const n = options[(i + d + options.length) % options.length];
        onChange(n.id);
        (e.currentTarget.querySelectorAll("button")[options.indexOf(n)] as HTMLButtonElement | undefined)?.focus();
      }}>
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={o.id === value} tabIndex={o.id === value ? 0 : -1} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Text input that commits on ↵ or blur; Esc puts the last value back. `pulse` replays the 600 ms field pulse (81 #5). */
export function TextField({ label, value, onCommit, placeholder, pulse, small, id }: {
  label: string; value: string; onCommit: (v: string) => void; placeholder?: string; pulse?: number; small?: boolean; id?: string;
}) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input id={id} className={`cs-input${small ? " cs-input--sm" : ""}`} aria-label={label} value={v} placeholder={placeholder} key={pulse ? `p${pulse}` : undefined}
      data-pulse={pulse ? "" : undefined}
      onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onCommit(v)}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { e.preventDefault(); setV(value); } }} />
  );
}

/**
 * A template field ("Since {since} · {sources}"): a plain input with a mirror behind it that paints each {token} as a
 * chip, so the text stays editable by keyboard and screen readers like any input.
 */
export function TokenField({ label, value, onCommit, insert, placeholder }: {
  label: string; value: string; onCommit: (v: string) => void; insert?: string[]; placeholder?: string;
}) {
  const [v, setV] = useState(value);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => setV(value), [value]);
  const add = (t: string) => {
    const el = ref.current;
    const at = el?.selectionStart ?? v.length;
    const next = `${v.slice(0, at)}{${t}}${v.slice(at)}`;
    setV(next); onCommit(next);
  };
  return (
    <div className="cs-token">
      <div className="cs-token__box">
        <div className="cs-token__mirror" aria-hidden>{v.split(/(\{[^}]*\})/).map((s, i) => (/^\{.*\}$/.test(s) ? <mark key={i}>{s}</mark> : <span key={i}>{s}</span>))}</div>
        <input ref={ref} className="cs-input cs-token__input" aria-label={label} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)}
          onBlur={() => v !== value && onCommit(v)}
          onKeyDown={(e) => { if (e.key === "Enter") ref.current?.blur(); if (e.key === "Escape") { e.preventDefault(); setV(value); } }} />
      </div>
      {insert && (
        <div className="cs-token__insert"><span>Insert:</span>
          {insert.map((t) => <button key={t} type="button" className="token" onMouseDown={(e) => e.preventDefault()} onClick={() => add(t)}>{`{${t}}`}</button>)}
        </div>
      )}
    </div>
  );
}

/** "Chicagoland Apartment Association" from a client slug; numbers and names pass through. */
export const prettyValue = (v: unknown) => {
  const s = Array.isArray(v) ? v.join(", ") : String(v);
  return /^[a-z0-9]+(-[a-z0-9]+)+$/.test(s) ? s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") : s;
};
